import styles from './TermsOfService.module.css';

export default function TermsOfService() {
  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Terms of Service</h1>
        <div className={styles.policyContent}>
          <section className={styles.section}>
            <p className={styles.effectiveDate}>Effective Date: February 21, 2026</p>
            <p>
              These Terms of Service ("Terms") govern your use of MyPeopleDatabase software and
              related services ("Service") provided by Object Nirvana, Inc. ("we," "us," or "our"),
              a Missouri corporation. By using, downloading, purchasing, or creating an account for
              MyPeopleDatabase, you agree to be bound by these Terms. If you do not agree to these
              Terms, do not use the Service.
            </p>
          </section>

          <section className={styles.section}>
            <h2>1. Description of Service</h2>
            <p>
              MyPeopleDatabase is a desktop contact management application that allows you to
              organize, track, and manage your personal and business contacts. The Service includes
              the desktop software application, the MyPeopleDatabase website, user accounts, and any
              related online features.
            </p>
          </section>

          <section className={styles.section}>
            <h2>2. License Grant</h2>
            <p>
              Upon purchase, we grant you a <strong>personal, non-exclusive, non-transferable,
              revocable license</strong> to install and use MyPeopleDatabase on up to five (5)
              devices that you personally own or control. This license is for your individual use
              only and may not be shared with, transferred to, or used by other individuals.
            </p>
            <p>You may not:</p>
            <ul>
              <li>Resell, sublicense, lease, or distribute the software to any third party</li>
              <li>Copy, modify, adapt, or create derivative works of the software</li>
              <li>Reverse-engineer, decompile, or disassemble the software</li>
              <li>Remove or alter any proprietary notices, labels, or marks</li>
              <li>Use the software for any unlawful purpose</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>3. Pricing and Payment</h2>
            <p>MyPeopleDatabase is available under two pricing options:</p>
            <ul>
              <li>
                <strong>One-Time Purchase:</strong> A single payment granting lifetime access to the
                software under the terms of this license.
              </li>
              <li>
                <strong>Annual Subscription:</strong> A recurring annual payment granting access to
                the software for the duration of the subscription period.
              </li>
            </ul>
            <p>
              All prices are listed in U.S. dollars and are subject to change. Any price changes
              will not affect existing active subscriptions until the next renewal period. Payments
              are processed securely through PayPal.
            </p>
          </section>

          <section className={styles.section}>
            <h2>4. Refund Policy</h2>
            <p>
              <strong>All sales are final. No refunds will be issued</strong> for one-time purchases
              or subscription payments. We encourage you to download and evaluate the software
              before purchasing.
            </p>
          </section>

          <section className={styles.section}>
            <h2>5. Subscription Cancellation</h2>
            <p>
              You may cancel your annual subscription at any time. Upon cancellation, you will
              retain full access to the software for the remainder of your current subscription
              period. Your subscription will not renew for the following year.{' '}
              <strong>
                Cancellations are not prorated and no partial refunds will be issued for unused
                time within the current subscription period.
              </strong>
            </p>
          </section>

          <section className={styles.section}>
            <h2>6. User Accounts</h2>
            <p>
              Certain features of the Service require you to create an account. You are responsible
              for:
            </p>
            <ul>
              <li>Providing accurate and complete registration information</li>
              <li>Maintaining the security and confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
            </ul>
            <p>
              You must notify us immediately of any unauthorized use of your account. We reserve the
              right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section className={styles.section}>
            <h2>7. Data Ownership and Privacy</h2>
            <p>
              <strong>You own 100% of your data.</strong> All contact information and personal data
              you enter into MyPeopleDatabase is stored locally on your device. We do not access,
              collect, transmit, or monetize your contact data. Your relationship data remains
              exclusively yours.
            </p>
            <p>
              For information about data we collect through the website and account services, please
              review our{' '}
              <a href="/privacy-policy" className={styles.link}>
                Privacy Policy
              </a>
              .
            </p>
          </section>

          <section className={styles.section}>
            <h2>8. Intellectual Property</h2>
            <p>
              MyPeopleDatabase, including all software, website content, branding, logos, design, and
              documentation, is the intellectual property of Object Nirvana, Inc. and is protected by
              copyright, trademark, and other intellectual property laws. Your license to use the
              software does not transfer any ownership rights.
            </p>
          </section>

          <section className={styles.section}>
            <h2>9. Affiliate Program</h2>
            <p>
              Participation in the MyPeopleDatabase Affiliate Program is subject to additional terms
              provided upon enrollment. We reserve the right to modify commission structures, program
              terms, or terminate the affiliate program at any time. Affiliate commissions are paid
              according to the schedule and terms outlined in the affiliate program documentation.
            </p>
          </section>

          <section className={styles.section}>
            <h2>10. Prohibited Uses</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Scrape, crawl, or use automated means to access the website</li>
              <li>Misrepresent your affiliation with Object Nirvana, Inc.</li>
              <li>Use the Service to harass, abuse, or harm others</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>11. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT
              WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
            </p>
          </section>

          <section className={styles.section}>
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, OBJECT NIRVANA, INC. SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
              NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR BUSINESS INTERRUPTION, ARISING OUT
              OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
            <p>
              OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR THE SERVICE SHALL NOT
              EXCEED THE AMOUNT YOU PAID TO US FOR THE SOFTWARE IN THE TWELVE (12) MONTHS PRECEDING
              THE CLAIM.
            </p>
          </section>

          <section className={styles.section}>
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause, if we reasonably believe you have violated these Terms. You may stop using the
              Service at any time. Upon termination, your license to use the software is revoked, but
              your locally stored data remains yours.
            </p>
          </section>

          <section className={styles.section}>
            <h2>14. Changes to These Terms</h2>
            <p>
              We reserve the right to update or modify these Terms at any time. Material changes will
              be posted on this page with an updated effective date. Your continued use of the
              Service after changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section className={styles.section}>
            <h2>15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State
              of Missouri, without regard to its conflict of law provisions. Any disputes arising
              from these Terms or the Service shall be resolved exclusively in the state or federal
              courts located in Missouri.
            </p>
          </section>

          <section className={styles.section}>
            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please{' '}
              <a href="/contact-us" className={styles.link}>
                contact us
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
